import React from 'react';

// Extracted from StudentProfile.js's renderLessonsSection (Phase 3 of the
// platform-health plan: split the file mechanically, without changing
// behavior). All state and handlers still live in the parent — this is
// purely the search bar + table markup, driven by props.
export default function LessonsTab({
  searchTerm,
  onSearchTermChange,
  onCreateLesson,
  lessonsLoading,
  sortedLessons,
  deletingLessonId,
  onStartLesson,
  onLessonStatusChange,
  onCopyLesson,
  onDeleteLesson,
}) {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="lessons-section-content">
      <div className="lessons-header">
        <div className="lessons-search-bar">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
            <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            placeholder="Search lessons..."
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
          />
        </div>
        <button className="create-lesson-btn" onClick={onCreateLesson}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Create a lesson
        </button>
      </div>
      <div className="lessons-table-container">
        <div className="lessons-table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Lesson Name</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lessonsLoading ? (
              <tr>
                <td colSpan="4" className="lessons-loading-cell">
                  <span className="lessons-loading-spinner" aria-hidden />
                  <span>Loading lessons…</span>
                </td>
              </tr>
            ) : sortedLessons.length > 0 ? (
              sortedLessons.map((lesson) => {
                // Use createdAt for date column (creation date, not schedule date)
                const creationDate = lesson.createdAt || lesson.created_at || (lesson.date && !lesson.from && !lesson.to && !lesson.timeFrom && !lesson.timeTo ? lesson.date : null);
                const lessonDate = formatDate(creationDate);

                return (
                <tr key={lesson.id} className={`lesson-row lesson-row-status-${lesson.status || 'in_progress'}`}>
                  <td>
                    <div className="lesson-name-cell">
                      <span
                        onClick={() => onStartLesson(lesson.id)}
                        className="clickable-lesson-name"
                        title="Click to edit lesson"
                      >
                        {lesson.title}
                      </span>
                    </div>
                  </td>
                  <td className="lesson-date-cell">{lessonDate}</td>
                  <td>
                    <select
                      value={lesson.status}
                      onChange={(e) => onLessonStatusChange(lesson.id, e.target.value)}
                      className="lesson-status-select"
                    >
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td>
                    <div className="lesson-actions">
                      <button
                        className="start-lesson-btn"
                        onClick={() => onStartLesson(lesson.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" fill="currentColor"/>
                        </svg>
                        Start
                      </button>
                      <button
                        className="copy-lesson-btn"
                        onClick={() => onCopyLesson(lesson)}
                        title="Copy Lesson"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                        </svg>
                      </button>
                      <button
                        className="delete-lesson-btn"
                        onClick={() => onDeleteLesson(lesson.id)}
                        title="Delete Lesson"
                        disabled={deletingLessonId === lesson.id}
                      >
                        {deletingLessonId === lesson.id ? (
                          <span className="delete-lesson-btn-spinner" aria-hidden />
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9ZM15.5 4L14.5 3H9.5L8.5 4H5V6H19V4H15.5Z" fill="currentColor"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="no-lessons-message">No lessons found.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
